import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Crown, Trophy, Percent, ShoppingBag, Gift, CheckCircle2, XCircle } from "lucide-react";
import { api, eur } from "../lib/api";
import { useAuth } from "../context/AuthContext";

export default function AreaSocio() {
  const { user } = useAuth();
  const [entries, setEntries] = useState([]);
  const [orders, setOrders] = useState([]);
  const [offers, setOffers] = useState([]);

  useEffect(() => {
    api.get("/me/entries").then(({ data }) => setEntries(data)).catch(() => {});
    api.get("/me/orders").then(({ data }) => setOrders(data)).catch(() => {});
    api.get("/offers").then(({ data }) => setOffers(data)).catch(() => {});
  }, []);

  if (!user) return null;

  return (
    <div className="max-w-7xl mx-auto px-5 lg:px-8 py-14 lg:py-20" data-testid="area-socio-page">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
        <p className="overline-gold mb-3">Área de socio</p>
        <h1 className="text-3xl lg:text-5xl text-white font-light tracking-tight">
          Hola, <span className="text-gold italic">{user.name}</span>
        </h1>
      </motion.div>

      <div className="mt-10 grid grid-cols-1 sm:grid-cols-3 gap-6">
        <div className="card-dark p-6" data-testid="membership-status-card">
          <Crown size={22} strokeWidth={1.25} className="text-gold mb-4" />
          <p className="text-xs uppercase tracking-[0.15em] text-white/40">Membresía</p>
          {user.is_member ? (
            <>
              <p className="mt-2 flex items-center gap-2 text-gold font-medium"><CheckCircle2 size={16} /> Activa</p>
              <p className="mt-1 text-white/50 text-sm">Hasta {user.member_until?.slice(0, 10)}</p>
            </>
          ) : (
            <>
              <p className="mt-2 flex items-center gap-2 text-white/60 font-medium"><XCircle size={16} /> Inactiva</p>
              <Link to="/hazte-socio" className="btn-gold mt-4 !px-5 !py-2.5" data-testid="activate-membership-btn">Activar por 100 €/año</Link>
            </>
          )}
        </div>
        <div className="card-dark p-6" data-testid="points-card">
          <Trophy size={22} strokeWidth={1.25} className="text-gold mb-4" />
          <p className="text-xs uppercase tracking-[0.15em] text-white/40">Puntos Premium</p>
          <p className="font-heading text-4xl text-white mt-2" data-testid="points-value">{user.points}</p>
          <p className="mt-1 text-white/50 text-xs">1 punto por cada euro gastado en la tienda</p>
        </div>
        <div className="card-dark p-6" data-testid="entries-card">
          <Gift size={22} strokeWidth={1.25} className="text-gold mb-4" />
          <p className="text-xs uppercase tracking-[0.15em] text-white/40">Mis Sorteos</p>
          <p className="font-heading text-4xl text-white mt-2" data-testid="entries-count">{entries.length}</p>
          <Link to="/sorteos-exclusivos" className="mt-1 inline-block text-gold text-xs uppercase tracking-[0.15em] hover:underline underline-offset-4">
            Ver exclusivos →
          </Link>
        </div>
      </div>

      <div className="mt-14 grid grid-cols-1 lg:grid-cols-2 gap-12">
        <section data-testid="my-entries-section">
          <h2 className="text-2xl text-white font-medium mb-6 flex items-center gap-3"><Gift size={20} className="text-gold" /> Mis participaciones</h2>
          {entries.length === 0 ? (
            <p className="text-white/40 text-sm font-light" data-testid="no-entries">Aún no participas en ningún sorteo.</p>
          ) : (
            <div className="space-y-3">
              {entries.map((e) => (
                <div key={e.id} className="card-dark p-5 flex items-center justify-between gap-4" data-testid={`entry-${e.id}`}>
                  <div>
                    <p className="text-white font-medium text-sm">{e.raffle?.title || "Sorteo"}</p>
                    <p className="text-white/40 text-xs mt-1 uppercase tracking-[0.1em]">
                      {e.raffle?.type === "exclusive" ? "Exclusivo" : "Gratuito"} · {e.raffle?.status === "open" ? "Abierto" : "Cerrado"}
                    </p>
                  </div>
                  {e.raffle?.winner && (
                    <span className={`text-xs font-bold px-3 py-1.5 ${e.raffle.winner.email === user.email ? "bg-gold text-black" : "bg-white/5 text-white/50"}`}>
                      {e.raffle.winner.email === user.email ? "¡GANADOR!" : `Ganador: ${e.raffle.winner.name}`}
                    </span>
                  )}
                </div>
              ))}
            </div>
          )}
        </section>

        <section data-testid="my-orders-section">
          <h2 className="text-2xl text-white font-medium mb-6 flex items-center gap-3"><ShoppingBag size={20} className="text-gold" /> Mis pedidos</h2>
          {orders.length === 0 ? (
            <p className="text-white/40 text-sm font-light" data-testid="no-orders">No tienes pedidos todavía.</p>
          ) : (
            <div className="space-y-3">
              {orders.map((o) => (
                <div key={o.id} className="card-dark p-5" data-testid={`order-${o.order_number}`}>
                  <div className="flex items-center justify-between">
                    <p className="text-white font-medium text-sm">{o.kind === "membership" ? "Membresía anual" : `Pedido ${o.order_number}`}</p>
                    <span className={`text-[10px] uppercase tracking-[0.15em] px-3 py-1 ${
                      o.status === "paid" ? "bg-gold/15 text-gold" : o.status === "failed" ? "bg-red-900/40 text-red-300" : "bg-white/5 text-white/50"
                    }`}>
                      {o.status === "paid" ? "Pagado" : o.status === "failed" ? "Fallido" : "Pendiente"}
                    </span>
                  </div>
                  <p className="text-sand text-sm mt-2">{eur(o.total_cents / 100)} · {o.method === "bizum" ? "Bizum" : "Tarjeta"}</p>
                </div>
              ))}
            </div>
          )}
        </section>
      </div>

      <section className="mt-14" data-testid="offers-section">
        <h2 className="text-2xl text-white font-medium mb-6 flex items-center gap-3"><Percent size={20} className="text-gold" /> Ofertas para socios</h2>
        {!user.is_member ? (
          <div className="card-dark p-8 text-center" data-testid="offers-locked">
            <p className="text-white/50 font-light">Las ofertas son exclusivas para socios con membresía activa.</p>
            <Link to="/hazte-socio" className="btn-gold mt-6" data-testid="offers-unlock-btn">Hazte Socio</Link>
          </div>
        ) : offers.length === 0 ? (
          <p className="text-white/40 text-sm font-light">No hay ofertas activas ahora mismo.</p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {offers.map((o) => (
              <div key={o.id} className="card-dark p-6" data-testid={`offer-${o.id}`}>
                <p className="text-lg text-white font-medium">{o.title}</p>
                <p className="mt-2 text-white/55 text-sm font-light">{o.description}</p>
                {o.code && <p className="mt-4 inline-block border border-dashed border-gold/50 text-gold text-xs tracking-[0.2em] px-4 py-2">{o.code}</p>}
                {o.valid_until && <p className="mt-3 text-white/35 text-[10px] uppercase tracking-[0.15em]">Válida hasta {o.valid_until}</p>}
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
